import React, { useContext, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { authAPI } from '../services/api';

const formatPhoneNumber = (value, countryCode) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (countryCode === '+1') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  if (countryCode === '+91') {
    return digits.length > 5 ? `${digits.slice(0, 5)} ${digits.slice(5, 10)}` : digits;
  }
  if (countryCode === '+44') {
    return digits.length > 4 ? `${digits.slice(0, 4)} ${digits.slice(4, 10)}` : digits;
  }
  if (countryCode === '+971') {
    return digits.length > 2 ? `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 9)}` : digits;
  }
  if (countryCode === '+61') {
    return digits.length > 4 ? `${digits.slice(0, 4)} ${digits.slice(4, 10)}` : digits;
  }
  return digits;
};

export default function Login({ initialMode = false }) {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [isSignUp, setIsSignUp] = useState(initialMode);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    whatsappPhone: '',
    phoneCountry: '+91',
    whatsappCountry: '+91',
    sameWhatsapp: false,
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (event) => {
    const fieldName = event.target.name;
    const countryCode = fieldName === 'phone' ? form.phoneCountry : form.whatsappCountry;
    const formattedValue = formatPhoneNumber(event.target.value, countryCode);
    setForm({
      ...form,
      [fieldName]: formattedValue,
      ...(fieldName === 'phone' && form.sameWhatsapp
        ? { whatsappPhone: formattedValue, whatsappCountry: form.phoneCountry }
        : {})
    });
  };

  const handleCountryChange = (event) => {
    const fieldName = event.target.name;
    const numberField = fieldName === 'phoneCountry' ? 'phone' : 'whatsappPhone';
    setForm({
      ...form,
      [fieldName]: event.target.value,
      [numberField]: formatPhoneNumber(form[numberField], event.target.value),
      ...(fieldName === 'phoneCountry' && form.sameWhatsapp
        ? { whatsappCountry: event.target.value, whatsappPhone: formatPhoneNumber(form.phone, event.target.value) }
        : {})
    });
  };

  const handleSameWhatsappChange = (event) => {
    const sameWhatsapp = event.target.checked;
    setForm({
      ...form,
      sameWhatsapp,
      ...(sameWhatsapp ? { whatsappCountry: form.phoneCountry, whatsappPhone: form.phone } : {})
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = isSignUp
        ? {
          name: form.name,
          email: form.email,
          phone: `${form.phoneCountry}${form.phone.replace(/\D/g, '')}`,
          whatsappPhone: `${form.sameWhatsapp ? form.phoneCountry : form.whatsappCountry}${(form.sameWhatsapp ? form.phone : form.whatsappPhone).replace(/\D/g, '')}`,
          password: form.password
        }
        : { email: form.email, password: form.password };

      const response = isSignUp
        ? await authAPI.signup(payload)
        : await authAPI.login({ email: form.email, password: form.password });

      const token = response.data.access_token || response.data.token;
      const userData = response.data.user;
      login(token, userData, response.data.refresh_token);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <h1>Welcome</h1>
        <p>Sign in to your account or create a new one</p>

        <div className="auth-toggle">
          <button
            type="button"
            className={!isSignUp ? 'active' : ''}
            onClick={() => setIsSignUp(false)}
          >
            Sign In
          </button>
          <button
            type="button"
            className={isSignUp ? 'active' : ''}
            onClick={() => setIsSignUp(true)}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {isSignUp && (
            <>
              <label>Name</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
              />
            </>
          )}

          {isSignUp && (
            <>
              <label>Phone number</label>
              <div className="phone-input-group">
                <select name="phoneCountry" value={form.phoneCountry} onChange={handleCountryChange} aria-label="Phone country code">
                  <option value="+91">India (+91)</option>
                  <option value="+1">United States (+1)</option>
                  <option value="+44">United Kingdom (+44)</option>
                  <option value="+971">UAE (+971)</option>
                  <option value="+61">Australia (+61)</option>
                </select>
                <input type="tel" name="phone" value={form.phone} onChange={handlePhoneChange} placeholder="Enter phone number" inputMode="numeric" pattern="[0-9 ()-]{7,20}" required />
              </div>
              <label className="same-number-option">
                <input type="checkbox" checked={form.sameWhatsapp} onChange={handleSameWhatsappChange} />
                WhatsApp number is the same as phone number
              </label>
              <label>WhatsApp number</label>
              <div className="phone-input-group">
                <select name="whatsappCountry" value={form.whatsappCountry} onChange={handleCountryChange} aria-label="WhatsApp country code">
                  <option value="+91">India (+91)</option>
                  <option value="+1">United States (+1)</option>
                  <option value="+44">United Kingdom (+44)</option>
                  <option value="+971">UAE (+971)</option>
                  <option value="+61">Australia (+61)</option>
                </select>
                <input type="tel" name="whatsappPhone" value={form.whatsappPhone} onChange={handlePhoneChange} placeholder="Enter WhatsApp number" inputMode="numeric" pattern="[0-9 ()-]{7,20}" required disabled={form.sameWhatsapp} />
              </div>
              <small className="whatsapp-purpose-note">We’ll contact you on WhatsApp for measurements and order updates.</small>
            </>
          )}

          <label>Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="you@example.com"
          />

          <label>Password</label>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            placeholder="********"
          />

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Please wait...' : isSignUp ? 'Sign Up' : 'Sign In'}
          </button>
        </form>

        {!isSignUp && (
          <button type="button" className="auth-bottom-signup" onClick={() => setIsSignUp(true)}>
            New here? Sign Up
          </button>
        )}

        <Link to="/" className="back-home-link">← Back to Home</Link>
      </div>
    </div>
  );
}
